import axios from 'axios';

import store from 'store/store';
import { AuthActions } from 'store/slices/auth';

import createAuthRefreshInterceptor from 'axios-auth-refresh';

const URL = `${process.env.REACT_APP_API_URL}`;

const DEFAULT_HEADERS: Record<string, string> = {
  Accept: 'application/json',
  'Content-Type': 'application/json',
  'ngrok-skip-browser-warning': 'true',
};

const instance = axios.create({
  baseURL: URL,
  headers: DEFAULT_HEADERS,
});

instance.interceptors.request.use((request) => {
  const state = store.getState();
  const jwtToken = state?.auth?.currentUser?.tokens?.jwtToken;

  if (jwtToken) {
    request.headers = {
      ...request.headers,
      Authorization: `Bearer ${jwtToken}`,
    };
  }

  return request;
});

const refreshAuthToken = async (payload: {
  username: string;
  refreshToken: string;
}) => {
  const { data } = await axios.post(`${URL}/auth/refresh-token`, payload, {
    headers: {
      'ngrok-skip-browser-warning': 'true',
    },
  });

  return data;
};

/**
 * Ends the session and sends the user to /login, then rejects the failed
 * request. Called for EVERY 401 the refresh flow cannot recover from (no
 * refresh token, non-refreshable token, or a refresh call that itself failed)
 * so the user is never left stuck retrying every request forever.
 */
const logoutAndReject = (message: string) => {
  store.dispatch(AuthActions.logout());

  window.location.href = '/login';

  return Promise.reject({
    response: {
      data: { message },
    },
  });
};

const refreshTokenLogic = async (callbackRequest: any) => {
  // Guard against a 401 with no JSON body — reading `.code` off undefined here
  // would throw and surface to the caller as a generic "Network error".
  const code = callbackRequest.response?.data?.code;

  // Only a genuine expiry is worth refreshing. Any other 401 (invalid/revoked
  // token, or a shape we don't recognise) cannot be fixed by refreshing.
  if (code !== 'expired_token') {
    return logoutAndReject('Your session has expired. Please log in again.');
  }

  const state = store.getState();
  const username = state?.auth?.currentUser?.username;
  const refreshToken = state?.auth?.currentUser?.tokens?.refreshToken;

  if (!refreshToken) {
    return logoutAndReject('Please log in.');
  }

  const data = await refreshAuthToken({
    username,
    refreshToken,
  }).catch(() => null);

  // Refresh rejected by the backend (e.g. the refresh token is itself expired
  // or revoked). Nothing recoverable — log out rather than trap the user.
  if (!data) {
    return logoutAndReject('Your session has expired. Please log in again.');
  }

  store.dispatch(AuthActions.updateTokens(data?.item?.tokens));

  callbackRequest.response.config.headers[
    'Authorization'
  ] = `Bearer ${data.item.tokens.jwtToken}`;

  return Promise.resolve();
};

const options = { statusCodes: [401] };

createAuthRefreshInterceptor(instance, refreshTokenLogic, options);

export default instance;
